import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Flex, Table } from '@televet/kibble-ui/chakra';
import { trpc } from '@/lib/trpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PageContainer } from '@/components/PageContainer';
import { DeleteDialog } from '@/components/DeleteDialog';
import { toaster } from '@/components/ui/toaster';
import { formatAudienceLabel } from '@/features/promptTemplates/audience';

export const Route = createFileRoute('/_authenticated/prompt-templates/')({
  beforeLoad: () => {
    return {
      pageTitle: 'Prompt Templates',
    };
  },
  loader: () => {
    return {
      pageTitle: 'Prompt Templates',
    };
  },
  component: PromptTemplates,
});

function PromptTemplates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState('');
  const [deleteTemplateId, setDeleteTemplateId] = useState<string | null>(null);

  const { data } = useQuery(
    trpc.promptTemplate.list.queryOptions({
      where: searchValue ? { name: searchValue } : {},
    })
  );

  const { mutate: deleteTemplate, isPending: isDeleting } = useMutation(
    trpc.promptTemplate.deleteOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Prompt template deleted',
          description: data.message,
        });
        queryClient.invalidateQueries({
          queryKey: [['promptTemplate', 'list']],
        });
        setDeleteTemplateId(null);
      },
      onError: (error) => {
        const errorMessage = error.message || 'Failed to delete prompt template';
        toaster.error({
          title: 'Failed to delete prompt template',
          description: errorMessage,
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const handleDelete = (templateId: string) => {
    setDeleteTemplateId(templateId);
  };

  const confirmDelete = () => {
    if (deleteTemplateId) {
      deleteTemplate({ id: deleteTemplateId });
    }
  };

  return (
    <PageContainer>
      <Flex direction="column" gap={4} p={8} width="100%">
        <Flex justify="space-between" align="center">
          <TextInput
            placeholder="Search by name..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            maxW="300px"
          />
          <Button onClick={() => navigate({ to: '/prompt-templates/add' })}>
            Add Prompt Template
          </Button>
        </Flex>
        <Table.Root size="lg" width="100%">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Order</Table.ColumnHeader>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Agent</Table.ColumnHeader>
              <Table.ColumnHeader>Audience</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Prompt</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Published</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data?.promptTemplates.map((template) => (
              <Table.Row key={template.id}>
                <Table.Cell>{template.order}</Table.Cell>
                <Table.Cell>{template.name}</Table.Cell>
                <Table.Cell>{template.agent.name}</Table.Cell>
                <Table.Cell>
                  {formatAudienceLabel(template.audience, template.hasInvalidAudience)}
                </Table.Cell>
                <Table.Cell textAlign="end">{template.prompt}</Table.Cell>
                <Table.Cell textAlign="end">{template.isPublished ? 'Yes' : 'No'}</Table.Cell>
                <Table.Cell textAlign="end">
                  <Flex gap={2} justify="flex-end">
                    <Button
                      iconName="pen"
                      variant="ghostNeutral"
                      size="sm"
                      onClick={() => navigate({ to: `/prompt-templates/${template.id}` })}
                    />
                    <Button
                      iconName="trashcan"
                      variant="ghostDestructive"
                      size="sm"
                      onClick={() => handleDelete(template.id)}
                    />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Flex>
      <DeleteDialog
        isOpen={!!deleteTemplateId}
        title="Delete Prompt Template"
        description={
          deleteTemplateId
            ? `Are you sure you want to delete this prompt template? This action cannot be undone.`
            : ''
        }
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTemplateId(null)}
        isLoading={isDeleting}
        isDangerous
      />
    </PageContainer>
  );
}
